/**
 * Live request-feed + money-conservation panels (features v3 + v4-operator-ux).
 * Polls the simulator's own /feed and /conservation. Each feed row is one executed journey
 * shown as a human narrative (FR-TS-122); clicking a row pins its full (redacted)
 * request/response into a fixed inspector panel (FR-TS-123) so it can be reconciled against
 * the bank backend without it scrolling away as the feed flows. A pause toggle freezes the
 * list (view-only) while reading.
 */

import { pinFeed, renderEmpty, selectedSeq } from './inspector.js';
import { getPersonaFilter } from './personas.js';
import { eur } from './money.js';

interface RequestTrace {
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody: unknown;
  responseStatus: number | null;
  responseBody: unknown;
}

export interface RequestEvent {
  seq: number;
  bank: string;
  user: string;
  scenario: string;
  outcome: string;
  httpStatus: number | null;
  latencyMs: number;
  description: string;
  detail: string;
  correlationId: string;
  trace: RequestTrace | null;
  /**
   * The driver declined this journey and made NO HTTP call (e.g. "skipped — low balance"). It is
   * shown so an operator can see why traffic is thin, but it is not a request: the server excludes
   * it from every metric, and every count derived here must exclude it too.
   */
  skipped?: boolean;
}

interface ConservationView {
  banks: { bank: string; totalCents: number | null; accounts: number; revenueCents: number | null }[];
  totalCents: number | null;
  openingTotalCents: number | null;
  driftCents: number | null;
}

const BANK_SHORT: Record<string, string> = { evaris_es: 'evaris', evairi_com: 'evairi' };
const MAX_ROWS = 150;

export type StatusFilter = 'all' | 'errors' | '4xx' | '5xx' | '2xx';
let statusFilter: StatusFilter = 'all';

export function getStatusFilter(): StatusFilter {
  return statusFilter;
}

export function setStatusFilter(doc: Document, filter: StatusFilter): void {
  statusFilter = filter;
  doc.querySelectorAll('[data-status-filter]').forEach((chip) => {
    const f = chip.getAttribute('data-status-filter');
    chip.setAttribute('data-active', f === filter ? 'true' : 'false');
  });
  if (state.events.length > 0) {
    renderFeed(doc, state.events);
  }
}

export function matchesStatusFilter(e: RequestEvent, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  // A skipped journey made no HTTP call; it is shown in "All" so an operator can see why traffic
  // is thin, but it must never be counted/classified as a real request outcome.
  if (e.skipped) return false;
  const status = e.httpStatus;
  const outcome = e.outcome;
  if (filter === 'errors') {
    return (
      (status !== null && status >= 400) ||
      outcome === 'client_error' ||
      outcome === 'server_error' ||
      outcome === 'transient_error'
    );
  }
  if (filter === '4xx') {
    return (status !== null && status >= 400 && status < 500) || outcome === 'client_error';
  }
  if (filter === '5xx') {
    return (status !== null && status >= 500 && status < 600) || outcome === 'server_error' || outcome === 'transient_error';
  }
  if (filter === '2xx') {
    return (status !== null && status >= 200 && status < 300) || outcome === 'success';
  }
  return true;
}

/** View state, preserved across polls. */
const state = {
  /** When true, the feed list is frozen (view-only; the run keeps going). */
  paused: false,
  /** Latest events seen, so the inspector can resolve a click even while paused. */
  events: [] as RequestEvent[],
};

function outcomeGlyph(outcome: string, skipped?: boolean): string {
  // A declined journey is neither a success nor a failure — nothing was attempted.
  if (skipped) return '–';
  if (outcome === 'success') return '✓';
  if (outcome === 'client_error') return '✗';
  if (outcome === 'server_error') return '!';
  return '~';
}

function span(cls: string, text: string): HTMLSpanElement {
  const s = document.createElement('span');
  s.className = cls;
  s.textContent = text;
  return s;
}

function selectRow(doc: Document, seq: number): void {
  const e = state.events.find((x) => x.seq === seq);
  if (e) pinFeed(doc, e); // pin into the shared inspector (FR-TS-123)
  // re-mark selection without waiting for the next poll
  doc.querySelectorAll('[data-feed] .ev').forEach((li) => {
    if (li.getAttribute('data-seq') === String(seq)) li.setAttribute('data-selected', 'true');
    else li.removeAttribute('data-selected');
  });
}

export function renderFeed(doc: Document, events: RequestEvent[]): void {
  state.events = events;
  const list = doc.querySelector('[data-feed]');
  if (!list) return;
  if (state.paused) return; // frozen list (view-only); inspector + conservation keep working

  // Apply persona filter (click avatar to show only that user's feed rows) + status filter.
  const persona = getPersonaFilter();
  const visible = events.filter((e) => {
    if (persona && e.user !== persona) return false;
    return matchesStatusFilter(e, statusFilter);
  });

  // Skip re-render when the visible content hasn't changed — prevents the feedin CSS animation
  // from replaying every 1500ms poll when no new events have arrived (v8 fix). Compare the full
  // ordered list of visible seqs so a status/persona filter change still re-renders even when the
  // first visible row happens to be the same.
  const incomingKey = visible.map((e) => e.seq).join(',');
  const renderedKey = list.getAttribute('data-visible-key');
  if (renderedKey === incomingKey && list.childElementCount > 0) return;
  list.setAttribute('data-visible-key', incomingKey);
  const rows = visible.slice(0, MAX_ROWS);
  list.replaceChildren(
    ...rows.map((e) => {
      const li = doc.createElement('li');
      li.className = 'ev';
      // `data-outcome` drives the row colour; a declined journey gets its own muted treatment so
      // it can never read as a successful request.
      li.setAttribute('data-outcome', e.skipped ? 'skipped' : e.outcome);
      if (e.skipped) li.setAttribute('data-skipped', 'true');
      li.setAttribute('data-seq', String(e.seq));
      if (e.seq === selectedSeq()) li.setAttribute('data-selected', 'true');

      const row = doc.createElement('div');
      row.className = 'ev-row';
      row.append(
        span(`bank b-${e.bank}`, BANK_SHORT[e.bank] ?? e.bank),
        span('oc', `${outcomeGlyph(e.outcome, e.skipped)} ${e.skipped ? 'skip' : e.httpStatus ?? '—'}`),
        span('desc', e.description),
        span('lat', e.skipped ? 'no call' : `${e.latencyMs}ms`),
      );
      li.append(row);
      li.title = e.correlationId;
      li.addEventListener('click', () => selectRow(doc, e.seq));
      return li;
    }),
  );
}

export function renderConservation(doc: Document, v: ConservationView): void {
  for (const b of v.banks) {
    const el = doc.querySelector(`[data-bal="${b.bank}"]`);
    if (el) el.textContent = eur(b.totalCents);
    const rev = doc.querySelector(`[data-rev="${b.bank}"]`);
    if (rev) rev.textContent = b.revenueCents === null ? '—' : eur(b.revenueCents);
  }
  const total = doc.querySelector('[data-bal="total"]');
  if (total) total.textContent = eur(v.totalCents);
  const drift = doc.querySelector('[data-bal="drift"]');
  if (drift) {
    drift.textContent = v.driftCents === null ? '—' : eur(v.driftCents);
    drift.parentElement?.setAttribute(
      'data-drift',
      v.driftCents === null ? 'unknown' : Math.abs(v.driftCents) <= 1 ? 'ok' : 'moved',
    );
  }
}

/** Wire the pause-feed toggle and status filter chips. Pause is VIEW-ONLY — it never touches the run or the breaker. */
export function initFeedControls(doc: Document = document): void {
  const btn = doc.querySelector<HTMLButtonElement>('[data-feed-pause]');
  if (btn) {
    const reflect = (): void => {
      btn.setAttribute('data-on', state.paused ? 'true' : 'false');
      btn.textContent = state.paused ? '▶ Resume feed' : '❚❚ Pause feed';
    };
    btn.addEventListener('click', () => {
      state.paused = !state.paused;
      reflect();
      if (!state.paused) renderFeed(doc, state.events); // catch up on resume
    });
    reflect();
  }

  doc.querySelectorAll<HTMLButtonElement>('[data-status-filter]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const f = (chip.getAttribute('data-status-filter') as StatusFilter) ?? 'all';
      setStatusFilter(doc, f);
    });
  });
}

async function getJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url, { cache: 'no-store' });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

export interface FeedCallbackArgs {
  events: RequestEvent[];
  conservationCents: number | null;
  driftCents: number | null;
}

export function initFeed(
  doc: Document = document,
  pollMs = 1500,
  onUpdate?: (args: FeedCallbackArgs) => void,
): void {
  initFeedControls(doc);
  renderEmpty(doc);
  const tick = async (): Promise<void> => {
    const [feed, cons] = await Promise.all([
      getJson<{ events: RequestEvent[] }>('/feed'),
      getJson<ConservationView>('/conservation'),
    ]);
    if (feed) renderFeed(doc, feed.events);
    if (cons) renderConservation(doc, cons);
    if (onUpdate) {
      onUpdate({
        events: feed?.events ?? [],
        conservationCents: cons?.totalCents ?? null,
        driftCents: cons?.driftCents ?? null,
      });
    }
  };
  void tick();
  window.setInterval(() => void tick(), pollMs);
}
